// 统一日志系统 —— 仅服务于 SDK 内部，不回流宿主。
//
// 设计要点：
//   * 等级：debug < info < warn < error < silent
//     - dev 默认 debug，prod 默认 info（生产环境保留 info+warn+error 以便事后排查）
//     - 可通过 setLevel() 运行时调整；URL ?logLevel=debug 也能覆盖
//   * 在内存里维护一份 ring buffer（最近 N 条），用于失败时一次性 dump
//   * 写入 IndexedDB 持久化（容量更大、跨刷新可见），失败回退到 sessionStorage
//   * 一次 SDK 调用 = 一个 sessionId（uuid，loader/embed 各算一次），所有日志都带上
//   * 同一份 logger 同时存在于 loader（host 域）和 embed（iframe 域），各自一份
//     存储；两边数据不共享，但通过 sessionId 串得起来
//
// 对外接口：仍然只有 `PalmMobileManager.start(...)`，不做任何破坏性变更。
// `Params.logLevel` 是新增可选字段，不传走默认。

import { LogLevel, type LogLevelName } from './protocol';

// ---------- 配置 ----------

const RING_BUFFER_MAX = 1000; // 内存里保留最近 1000 条
const PERSIST_DB_NAME = 'palm-mm-logs';
const PERSIST_STORE = 'logs';
const PERSIST_VERSION = 1;
const PERSIST_LIMIT = 5000; // IndexedDB 里保留最近 5000 条，老的轮转
const PERSIST_FLUSH_MS = 500; // 批量落盘节流
const PERSIST_FLUSH_MAX = 50;

// ---------- 类型 ----------

export interface LogEntry {
  /** ISO 时间戳 */
  ts: string;
  /** 数值时间戳（排序方便） */
  tms: number;
  level: LogLevelName;
  tag: string;
  message: string;
  meta?: Record<string, unknown>;
  /** 一次 SDK 会话 id（loader / embed 各自生成） */
  sessionId: string;
  /** 'loader' | 'embed' | 其他自定义 scope */
  scope: string;
  /**
   * 一次进入 AcquisitionView 的子会话 id；只在采集页生命周期内有值。
   * 与 sessionId 是 1:N 关系：用户可能在一次 SDK.start 内反复重试。
   */
  acqId?: string;
}

export interface LoggerOptions {
  scope: string;
  level?: LogLevelName;
  /** 是否落库；默认 true，环境不支持 IndexedDB 时自动降级 */
  persist?: boolean;
  /** 是否同时打到 console；默认 true */
  mirrorConsole?: boolean;
}

// ---------- 工具 ----------

const LEVEL_NUM: Record<LogLevelName, number> = {
  debug: LogLevel.Debug,
  info: LogLevel.Info,
  warn: LogLevel.Warn,
  error: LogLevel.Error,
  silent: LogLevel.Silent,
};

function safeUuid(): string {
  // crypto.randomUUID 不在所有目标设备上都可用，做一个简单回退
  const c = (typeof crypto !== 'undefined' ? crypto : undefined) as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  // RFC4122 v4 简化实现（非密码学安全，用于 traceId 足够）
  const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
  return `${rnd()}-${rnd().slice(0, 4)}-4${rnd().slice(0, 3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${rnd().slice(0, 3)}-${rnd()}${rnd().slice(0, 4)}`;
}

function readUrlOverrideLevel(): LogLevelName | undefined {
  if (typeof location === 'undefined') return undefined;
  try {
    const v = new URL(location.href).searchParams.get('logLevel');
    if (!v) return undefined;
    const k = v.toLowerCase() as LogLevelName;
    return k in LEVEL_NUM ? k : undefined;
  } catch {
    return undefined;
  }
}

// ---------- IndexedDB 持久化 ----------

class IdbStore {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private supported = typeof indexedDB !== 'undefined';
  private queue: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  isSupported() {
    return this.supported;
  }

  enqueue(entry: LogEntry) {
    if (!this.supported) return;
    this.queue.push(entry);
    if (this.queue.length >= PERSIST_FLUSH_MAX) {
      this.flush();
      return;
    }
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), PERSIST_FLUSH_MS);
    }
  }

  async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;
    const batch = this.queue;
    this.queue = [];
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(PERSIST_STORE, 'readwrite');
        const store = tx.objectStore(PERSIST_STORE);
        for (const e of batch) store.add(e);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      await this.trim(db).catch(() => undefined);
    } catch {
      this.supported = false;
    }
  }

  async readAll(): Promise<LogEntry[]> {
    if (!this.supported) return [];
    try {
      const db = await this.open();
      return await new Promise<LogEntry[]>((resolve, reject) => {
        const tx = db.transaction(PERSIST_STORE, 'readonly');
        const store = tx.objectStore(PERSIST_STORE);
        const req = store.getAll();
        req.onsuccess = () => resolve((req.result as LogEntry[]) ?? []);
        req.onerror = () => reject(req.error);
      });
    } catch {
      return [];
    }
  }

  async clear(): Promise<void> {
    if (!this.supported) return;
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(PERSIST_STORE, 'readwrite');
        tx.objectStore(PERSIST_STORE).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      // ignore
    }
  }

  private open(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(PERSIST_DB_NAME, PERSIST_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(PERSIST_STORE)) {
            const store = db.createObjectStore(PERSIST_STORE, {
              keyPath: 'id',
              autoIncrement: true,
            });
            store.createIndex('tms', 'tms');
            store.createIndex('sessionId', 'sessionId');
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }
    return this.dbPromise;
  }

  // 删除超过 PERSIST_LIMIT 条的最老记录
  private async trim(db: IDBDatabase) {
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(PERSIST_STORE, 'readwrite');
      const store = tx.objectStore(PERSIST_STORE);
      const countReq = store.count();
      countReq.onsuccess = () => {
        const count = countReq.result;
        if (count <= PERSIST_LIMIT) {
          resolve();
          return;
        }
        const toDelete = count - PERSIST_LIMIT;
        const cursorReq = store.openCursor();
        let deleted = 0;
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || deleted >= toDelete) {
            resolve();
            return;
          }
          cursor.delete();
          deleted += 1;
          cursor.continue();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      };
      countReq.onerror = () => reject(countReq.error);
    });
  }
}

// ---------- Logger 主体 ----------

class Logger {
  private level: LogLevelName;
  private readonly scope: string;
  private readonly buffer: LogEntry[] = [];
  private readonly mirrorConsole: boolean;
  private readonly persistence: IdbStore | null;
  private sessionId: string;
  private acqId: string | undefined;

  constructor(opts: LoggerOptions) {
    this.scope = opts.scope;
    this.mirrorConsole = opts.mirrorConsole ?? true;
    this.persistence = (opts.persist ?? true) ? new IdbStore() : null;
    this.sessionId = safeUuid();

    const urlLevel = readUrlOverrideLevel();
    const envDefault: LogLevelName =
      typeof import.meta !== 'undefined' && (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV ? 'debug' : 'info';
    this.level = urlLevel ?? opts.level ?? envDefault;

    // 页面卸载前最后一次落盘，避免丢日志
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => void this.persistence?.flush(), { capture: true });
      window.addEventListener('beforeunload', () => void this.persistence?.flush(), { capture: true });
    }
  }

  setLevel(level: LogLevelName) {
    this.level = level;
  }

  getLevel(): LogLevelName {
    return this.level;
  }

  /** 开启新会话（一次 SDK.start 对应一个）。返回 sessionId 便于上下文串联。 */
  newSession(externalId?: string): string {
    this.sessionId = externalId ?? safeUuid();
    return this.sessionId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  /** 设置/清除当前 acquisition 子会话 id；传 undefined 表示已离开采集页。 */
  setAcqId(id: string | undefined) {
    this.acqId = id;
  }

  getAcqId(): string | undefined {
    return this.acqId;
  }

  debug(tag: string, message: string, meta?: Record<string, unknown>) {
    this.write('debug', tag, message, meta);
  }
  info(tag: string, message: string, meta?: Record<string, unknown>) {
    this.write('info', tag, message, meta);
  }
  warn(tag: string, message: string, meta?: Record<string, unknown>) {
    this.write('warn', tag, message, meta);
  }
  error(tag: string, message: string, meta?: Record<string, unknown>) {
    this.write('error', tag, message, meta);
  }

  /** 内存里近 N 条（不读 IndexedDB），用于即时 dump */
  snapshot(): LogEntry[] {
    return this.buffer.slice();
  }

  /** 持久化的全量日志 */
  async readPersisted(): Promise<LogEntry[]> {
    if (!this.persistence) return [];
    await this.persistence.flush();
    return this.persistence.readAll();
  }

  async clearPersisted(): Promise<void> {
    await this.persistence?.clear();
    this.buffer.length = 0;
  }

  /** 把当前 ring buffer + 持久化日志合并成可下载的纯文本 */
  async exportText(): Promise<string> {
    const persisted = await this.readPersisted();
    return mergeLogEntries(persisted, this.buffer).map(formatLine).join('\n');
  }

  private write(level: LogLevelName, tag: string, message: string, meta?: Record<string, unknown>) {
    if (LEVEL_NUM[level] < LEVEL_NUM[this.level]) return;

    const now = Date.now();
    const entry: LogEntry = {
      ts: new Date(now).toISOString(),
      tms: now,
      level,
      tag,
      message,
      meta: meta && Object.keys(meta).length > 0 ? sanitizeMeta(meta) : undefined,
      sessionId: this.sessionId,
      scope: this.scope,
      acqId: this.acqId,
    };

    // ring buffer
    this.buffer.push(entry);
    if (this.buffer.length > RING_BUFFER_MAX) {
      this.buffer.splice(0, this.buffer.length - RING_BUFFER_MAX);
    }

    // mirror to console
    if (this.mirrorConsole) {
      const fn =
        level === 'debug'
          ? console.debug
          : level === 'info'
            ? console.info
            : level === 'warn'
              ? console.warn
              : console.error;
      const head = `[${this.scope}][${tag}]`;
      if (entry.meta) fn.call(console, head, message, entry.meta);
      else fn.call(console, head, message);
    }

    // persist
    this.persistence?.enqueue(entry);
  }
}

// meta 里如果是 Error / 含循环引用，JSON.stringify 会爆；这里做一次 best-effort 清洗
function sanitizeMeta(meta: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    out[k] = sanitizeValue(v);
  }
  return out;
}

function sanitizeValue(v: unknown, depth = 0): unknown {
  if (depth > 4) return '[truncated]';
  if (v == null) return v;
  if (v instanceof Error) {
    return { name: v.name, message: v.message, stack: v.stack };
  }
  if (typeof v === 'function') return `[fn ${v.name || 'anonymous'}]`;
  if (typeof v === 'bigint') return v.toString();
  if (typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x) => sanitizeValue(x, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    try {
      out[k] = sanitizeValue(val, depth + 1);
    } catch {
      out[k] = '[unserializable]';
    }
  }
  return out;
}

// persisted 可能与内存 buffer 有重叠（还没刷盘的部分）→ 去重 + 按时间排序
export function mergeLogEntries(persisted: LogEntry[], buffer: LogEntry[]): LogEntry[] {
  const seen = new Set<string>();
  const merged: LogEntry[] = [];
  for (const e of [...persisted, ...buffer]) {
    const k = `${e.tms}|${e.tag}|${e.message}|${e.sessionId}`;
    if (seen.has(k)) continue;
    seen.add(k);
    merged.push(e);
  }
  merged.sort((a, b) => a.tms - b.tms);
  return merged;
}

function formatLine(e: LogEntry): string {
  const meta = e.meta ? ` ${safeJson(e.meta)}` : '';
  const acq = e.acqId ? ` aid=${e.acqId}` : '';
  return `${e.ts} ${e.level.toUpperCase().padEnd(5)} [${e.scope}][${e.tag}] ${e.message}${meta} (sid=${e.sessionId}${acq})`;
}

export function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return '"[unserializable]"';
  }
}

// ---------- 单例 & 全局暴露 ----------

// scope 自动推断：iframe 内（embed）path 形如 /embed/，其它视为 loader / host。
function detectScope(): string {
  if (typeof window === 'undefined') return 'sdk';
  try {
    if (window.parent !== window) return 'embed';
  } catch {
    return 'embed';
  }
  return 'loader';
}

const SCOPE = detectScope();

export const logger = new Logger({ scope: SCOPE });

// 暴露到 window 方便控制台手动调试：
//   window.__palmLogger__.setLevel('debug')
//   window.__palmLogger__.exportText().then(t => console.log(t))
if (typeof window !== 'undefined') {
  (window as unknown as { __palmLogger__?: Logger }).__palmLogger__ = logger;
}

export type { Logger };
